import React, { useCallback, useState, useContext, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import styled from 'styled-components';
import {
  Avatar,
  Button,
  Heading,
  Tooltip as SuperopsTooltip,
  Modal,
  ButtonGroup,
  Page
} from '@superops/ui-design-system';
import nanoid from 'nanoid';
import { SVGIcon } from '@superops/super-icons';
import envConfig from '@nile/home/src/Config/env';
import {
  getTranslatedText,
  COLOR_MAP,
  TEXT_LENGTH,
  Editor,
  QueryBuilder,
  RunbookNodeActivatorContent,
  Loader,
  withErrorBoundary,
  COMMON_CONSTANTS
} from '@nile/components';
import { injectIntl, FormattedMessage } from 'react-intl';
import TicketPropertiesCard from '@nile/banyan/src/Containers/CardContainers/TicketPropertiesCard';
import {
  GET_TICKET_AUTOMATION_RULE_META,
  GET_ENTITY_PROPERTIES,
  GET_RUNBOOKS_TICKET_PROPERTY_META,
  TicketClient
} from '@nile/graphql';
import { useQuery } from '@apollo/client';
import { cloneDeep, isEmpty, omit } from 'lodash';

function transformUpdatePropertyData(input) {
  if (!input) {
    return [];
  }

  const result = [];

  if (input?.customFields) {
    for (const [key, value] of Object.entries(input?.customFields)) {
      result.push({ name: key, value: value });
    }
  }
  if (input) {
    for (const [key, value] of Object.entries(input)) {
      if (key !== 'customFields') {
        result.push({ name: key, value: value });
      }
    }
  }

  return result;
}

function UpdatePropertyActionModal(props) {
  const { intl, isAutoAction } = props;

  const [triggerUpdate, setTriggerUpdate] = useState([]);

  const editorRef = React.useRef(null);

  const formData = props?.autoActionformData || props?.data?.formData || {};

  const actionValueData =
    props?.autoActionformData?.actionValueData || formData?.actionValueData;
  const [configData, setConfigData] = useState(
    transformUpdatePropertyData(actionValueData?.ticket) || []
  );

  const parentActionId = formData?.parentActionId;
  const hasActionId = formData?.actionId;

  const [popoverActive, setPopoverActive] = useState(
    Boolean(
      (isAutoAction && !props?.autoActionformData) ||
        (!isAutoAction && !hasActionId)
    )
  );

  const showDeleteButton =
    (props.isAutoAction && props?.autoActionformData) ||
    (!props.isAutoAction && hasActionId);

  const [ticketFieldsMeta, setTicketFieldsMeta] = useState({
    getTicketAutomationRuleMetaInfo: {
      actionGroups: {
        PROPERTY_UPDATE: []
      }
    }
  });

  const context = {
    updateAutomationRule: () => {},
    value: {
      action: [
        {
          type: 'PROPERTY_UPDATE',
          data: transformUpdatePropertyData(actionValueData?.ticket) || []
        }
      ]
    }
  };

  const togglePopoverActive = useCallback(() => {
    setPopoverActive((active) => !active);
  }, []);

  const { data: ticketUpdateData, loading: ticketUpdateDataLoading } = useQuery(
    GET_RUNBOOKS_TICKET_PROPERTY_META,
    {
      client: TicketClient,
      variables: {},
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
      // skip:
      //   ticketFieldsMeta.getTicketAutomationRuleMetaInfo.actionGroups
      //     .PROPERTY_UPDATE.length > 1,
      onCompleted(res) {
        // setSelectedItems(res);

        const ticketFieldsMetaTemp = {
          getTicketAutomationRuleMetaInfo: {
            actionGroups: {
              PROPERTY_UPDATE:
                res?.getTicketMetaForRunbookAction?.propertyUpdate
            }
          }
        };
        setTicketFieldsMeta(ticketFieldsMetaTemp);
      },
      onError() {
        toast('error', 'error.page.config.default.title');
      }
    }
  );

  const {
    loading: entitiesLoading,
    error: entitiesError,
    data: allEntities
  } = useQuery(GET_ENTITY_PROPERTIES);

  const {
    loading: ticketAutomationMetaLoading,
    error: ticketAutomationError,
    data: ticketAutomationMeta
  } = useQuery(GET_TICKET_AUTOMATION_RULE_META, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NETWORK_ONLY,
    variables: {
      automationRuleType: 'EVENT_TRIGGERS'
    }
  });

  const preparedQueries = {
    id: `g-${nanoid()}`,
    rules: [],
    combinator: 'AND',
    not: false
  };
  const [query, setQuery] = useState(preparedQueries);

  const saveLoading = false;

  const activator = (
    <RunbookNodeActivatorContent
      nodeType="admin.runbooks.templateactions.updateproperty"
      nodeName={
        formData?.actionValueType === 'TICKET_UPDATE' ? formData?.name : ''
      }
      iconName="ReloadColoured"
      callBackFunction={togglePopoverActive}
    />
  );

  const updateConditions = (conditions) => {
    setConfigData(conditions);
  };

  const mapData = [
    { propertyName: 'field 1', propertyValue: 'value 1' },
    { propertyName: 'field 2', propertyValue: 'value 2' }
  ];

  function transformData(configData) {
    const data = cloneDeep(configData);
    const result = {};
    const customFields = {};

    data.forEach((item) => {
      if (item?.name.startsWith('udf')) {
        customFields[item?.name] = item?.value;
      } else {
        result[item?.name] = item?.value;
      }
    });

    if (Object.keys(customFields).length > 0) {
      result.customFields = customFields;
    }

    return result;
  }

  const onSave = () => {
    const result = [...configData.map((arg) => omit(arg, ['id']))];

    const formDataTemp = {
      name: '', // name returned in response.
      id: props.id,
      actionType: 'SINGLE',
      actionProcessCategory: props?.isAutoAction ? 'ASYNC' : 'SYNC',
      actionValueType: 'TICKET_UPDATE',
      actionValueData: {
        nodeId: props.id,
        ticket: transformData(result)
        // ticketUpdate: result
      }
    };

    if (hasActionId) {
      formDataTemp.actionId = hasActionId;
    }
    if (parentActionId) {
      formDataTemp.parentActionId = parentActionId;
    }

    if (props.isAutoAction) {
      props.autoActionSave(formDataTemp);
    } else {
      props.modifyNodeData(formDataTemp);
    }

    setPopoverActive(false);
  };

  useEffect(() => {
    if (!isEmpty(triggerUpdate)) {
      onSave();
    }
  }, [triggerUpdate]);

  useEffect(() => {
    if (actionValueData?.ticket) {
      context.value.action.push({
        type: 'PROPERTY_UPDATE',
        data: transformUpdatePropertyData(actionValueData?.ticket)
      });
    }
  }, [actionValueData?.ticket]);

  const handleNodeDelete = () => {
    setPopoverActive(false);

    if (props.isAutoAction) {
      props.handleAutoActionDelete(props.id);
    } else {
      props.data.handleNodeDelete(props.id, hasActionId);
    }
  };

  const handleNodeCancel = useCallback(() => {
    setPopoverActive(false);
    if (!props.isAutoAction && !hasActionId) {
      handleNodeDelete();
    } else if (props.isAutoAction && !props?.autoActionformData) {
      props.handleAutoActionDelete(props.id);
    }
  }, [hasActionId, handleNodeDelete]);

  return (
    <Modal
      large
      activator={activator}
      open={popoverActive}
      onClose={togglePopoverActive}
      showCloseButton={false}
      noScroll
    >
      <Page
        title={getTranslatedText(
          intl,
          'admin.runbooks.templateactions.updateproperty'
        )}
        contentHeight="110px"
        fullWidth
        variant="secondary"
        primaryAction={
          <ButtonGroup spacing="loose">
            {showDeleteButton && (
              <Button plainSecondary onClick={handleNodeDelete}>
                <div className="danger">
                  <FormattedMessage id="project.details.delete" />
                </div>
              </Button>
            )}
            <Button plainSecondary onClick={handleNodeCancel}>
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              primary
              icon={<SVGIcon name="Save" />}
              onClick={onSave}
              loading={saveLoading}
              disabled={
                configData.length === 0 ||
                configData.some((item) => !item.name || !item.value)
              }
            >
              <FormattedMessage id="common.save" />
            </Button>
          </ButtonGroup>
        }
      >
        <div className="p2040">
          {/* {mapData.map((item, index) => {
            return (
              <div key={index} className="flex center g10">
                <SVGIcon name="CheckBox" />
                <input type="text" value={item.propertyName} />
                <span>to</span>
                <input type="text" value={item.propertyValue} />
                <SVGIcon name="CircleMinus" />
              </div>
            );
          })} */}
          {(entitiesLoading ||
            ticketAutomationMetaLoading ||
            ticketUpdateDataLoading) && <Loader />}
          {!ticketAutomationMetaLoading &&
            !entitiesLoading &&
            !ticketUpdateDataLoading && (
              <TicketPropertiesCard
                context={context}
                allEntities={allEntities}
                ticketAutomationMeta={ticketFieldsMeta}
                intl={intl}
                handleUpdateParent={updateConditions}
                isRunBookAction
              />
            )}
        </div>
      </Page>
    </Modal>
  );
}

export default withErrorBoundary(injectIntl(UpdatePropertyActionModal));
